import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format, isPast } from "date-fns";
import { sv } from "date-fns/locale";
import { Clock, MapPin, KeyRound, Key } from "lucide-react";
import PaymentStatusBadge from "@/components/booking/PaymentStatusBadge";
import { BookingActions } from "./BookingActions";
import type { BookingWithCourt, BookingPaymentRow, CheckinState } from "@/hooks/useMyBookings";
import { useTranslation } from "react-i18next";
import { formatPrice } from "@/utils/formatPrice";
import { resolveBookingDisplayAmount } from "@/lib/bookingDisplayAmount";

interface BookingCardProps {
  booking: BookingWithCourt;
  showCancel?: boolean;
  /** All bookings in current list (used to count multi-court bookings) */
  allBookings?: BookingWithCourt[];
  payments: BookingPaymentRow[];
  userId: string | undefined;
  payingBookingId: string | null;
  cancelIsPending: boolean;
  checkinIsPending: boolean;
  canCancel: (booking: BookingWithCourt) => boolean;
  getCheckinState: (booking: BookingWithCourt) => CheckinState;
  onSelect: (bookingId: string) => void;
  onCheckin: (bookingId: string) => void;
  onPayNow: (booking: BookingWithCourt) => void;
  onReschedule: (booking: BookingWithCourt) => void;
  onCancel: (bookingId: string) => void;
}

export function BookingCard({
  booking,
  showCancel,
  allBookings,
  payments,
  userId,
  payingBookingId,
  cancelIsPending,
  checkinIsPending,
  canCancel,
  getCheckinState,
  onSelect,
  onCheckin,
  onPayNow,
  onReschedule,
  onCancel,
}: BookingCardProps) {
  const { t } = useTranslation("booking");
  const bookingPayments = payments.filter((p: BookingPaymentRow) => p.booking_id === booking.id);
  const userPayment = bookingPayments.find((p: BookingPaymentRow) => p.user_id === userId);
  // Samma källa som desktop-tabellen — kortet visade tidigare bara den egna
  // betalradens `amount`, vilket gömde 0 kr och saknade price_total-fallbacken.
  const displayAmount = resolveBookingDisplayAmount(booking, payments, userId, allBookings);
  // Sibling-badge fix: paid group siblings have no own payment row (payment hangs
  // on the primary via booking_group_id) — fall back to booking.payment_status.
  const paymentStatus = userPayment?.status ?? (bookingPayments.length > 0 ? bookingPayments[0].status : (booking.payment_status ?? "unpaid"));
  const showPayButton = userPayment?.status === "pending" && booking.status === "confirmed" && !isPast(new Date(booking.start_time));
  const checkin = booking.status === "confirmed" && showCancel ? getCheckinState(booking) : null;

  return (
    <Card className="cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => onSelect(booking.id)}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="font-semibold">
              {format(new Date(booking.start_time), "EEEE d MMM", { locale: sv })}
            </p>
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Clock className="h-3.5 w-3.5" />
              {format(new Date(booking.start_time), "HH:mm")} – {format(new Date(booking.end_time), "HH:mm")}
              <span className="ml-1">({booking.duration_minutes} min)</span>
            </div>
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <MapPin className="h-3.5 w-3.5" />
              {booking.courts?.name} – {booking.courts?.halls?.name}
              {booking.booking_group_id && (
                <Badge variant="secondary" className="text-[10px] py-0 ml-1">
                  {(allBookings?.filter((b: BookingWithCourt) => b.booking_group_id === booking.booking_group_id) ?? []).length} banor
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <PaymentStatusBadge status={paymentStatus} />
              {displayAmount.kind === "amount" ? (
                <div className="flex items-center gap-1">
                  <span className="text-xs font-medium">{formatPrice(displayAmount.money)}</span>
                  {displayAmount.note && (
                    <span className="text-[10px] text-muted-foreground">({displayAmount.note})</span>
                  )}
                </div>
              ) : displayAmount.kind === "groupSibling" ? (
                <span className="text-[10px] text-muted-foreground">{t("partOfGroupBooking")}</span>
              ) : null}
            </div>
          </div>
          {booking.status === "confirmed" && (
            <div className="flex items-center gap-1 rounded-md bg-primary/10 px-2 py-1">
              <KeyRound className="h-3.5 w-3.5 text-primary" />
              <span className="font-mono text-sm font-bold text-primary">{booking.pin_code}</span>
            </div>
           )}
        </div>
        {/* Access code display */}
        {(() => {
          const hallData = booking.courts?.halls;
          const accessCode = hallData?.access_code;
          const sendBefore = hallData?.access_code_send_minutes_before ?? 60;
          const minutesUntil = (new Date(booking.start_time).getTime() - Date.now()) / 60000;
          const showCode = accessCode && minutesUntil <= sendBefore && minutesUntil > -30 && booking.status === "confirmed";
          if (!showCode) return null;
          return (
            <div className="flex items-center gap-2 bg-accent/50 rounded-md px-3 py-1.5">
              <Key className="h-4 w-4 text-primary" />
              <div>
                <p className="text-[10px] text-muted-foreground">Entrékod</p>
                <p className="font-mono font-bold text-sm">{accessCode}</p>
              </div>
            </div>
          );
        })()}
        <BookingActions
          booking={booking}
          showCancel={showCancel}
          userPayment={userPayment}
          showPayButton={showPayButton}
          checkin={checkin}
          payingBookingId={payingBookingId}
          canCancel={canCancel}
          cancelIsPending={cancelIsPending}
          checkinIsPending={checkinIsPending}
          onCheckin={onCheckin}
          onPayNow={onPayNow}
          onReschedule={onReschedule}
          onCancel={onCancel}
        />
        {booking.status === "cancelled" && (
          <span className="text-xs text-destructive font-medium">Avbokad</span>
        )}
      </CardContent>
    </Card>
  );
}
